/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { inject } from "mobx-react";
import Grabber from "dc-preview-utils/src/components/selection/Grabber";
import { getSelRightX, getSelRightY, getTouchSelPageIndex } from "dc-preview-utils/src/selectors/touchSelectors";
import { pageContentHandler } from "pageContentHandler";
import * as CommonConstants from "dc-preview-utils/src/Constants/constants";
import addEventListener from "dc-preview-utils/src/Utils/DomUtils";
import { getPageHandle } from "selectors/pageSelectors";
import * as constants from "constants/constants";
import { getLeftMostQuad } from "dc-preview-utils/src/selectors/pageHighlightsSelectors";
import { getPageZoomLevel } from "selectors/zoom";

let extendSelection = false;
let removeMouseUpHandler;

function handleGrabberMoveHelper(stores, x, y) {
  stores.touchSelStore.setIsTouchSelectionInProgress(true);
  let pageIndex = getTouchSelPageIndex(stores);
  let pageRect = getPageHandle(stores, pageIndex).getBoundingClientRect();
  let xInPage = x - pageRect.left;
  let yInPage = y - pageRect.top;
  const pageZoomLevel = getPageZoomLevel(stores, pageIndex);
  pageContentHandler
    .getTextManagerInstance()
    .handleTouchMove(
      pageRect,
      getSelRightX(stores) * pageZoomLevel,
      getSelRightY(stores) * pageZoomLevel,
      xInPage,
      yInPage,
      pageIndex,
      0
    );
}

function getTop(quad, grabberSize) {
  const { topLeft, bottomRight } = quad;
  const top = topLeft.y;
  const bottom = bottomRight.y;
  if (top < bottom) {
    return top - grabberSize;
  }
  return top;
}

function getLeft(quad, grabberSize) {
  const { topLeft, bottomRight } = quad;
  const left = topLeft.x;
  const right = bottomRight.x;
  if (left < right) {
    return left - grabberSize;
  }
  return left;
}

function computeLeftMostQuad(stores, pageIndex) {
  const pageZoomLevel = getPageZoomLevel(stores, pageIndex);
  const unzoomedLeftMostQuad = getLeftMostQuad(stores, pageIndex);
  let leftMostQuad;
  if (unzoomedLeftMostQuad) {
    leftMostQuad = unzoomedLeftMostQuad.transform((x, y) => ({
      x: x * pageZoomLevel,
      y: y * pageZoomLevel
    }));
  }
  return leftMostQuad;
}

function mapStoreToProps(stores, ownProps) {
  let pageIndex = getTouchSelPageIndex(stores);
  const leftMostQuad = computeLeftMostQuad(stores, pageIndex);
  const isLeftMostQuadValid = leftMostQuad && leftMostQuad.quadPoints.length;

  return {
    top: (function() {
      if (isLeftMostQuadValid) {
        return getTop(leftMostQuad, CommonConstants.GRABBER_SIZE);
      }
    })(),
    left: (function() {
      if (isLeftMostQuadValid) {
        return getLeft(leftMostQuad, CommonConstants.GRABBER_SIZE);
      }
    })(),
    outerTop: (function() {
      if (isLeftMostQuadValid) {
        return getTop(leftMostQuad, CommonConstants.OUTER_GRABBER_SIZE);
      }
    })(),
    outerLeft: (function() {
      if (isLeftMostQuadValid) {
        return getLeft(leftMostQuad, CommonConstants.OUTER_GRABBER_SIZE);
      }
    })(),
    handleTouchEnd: event => {
      stores.touchSelStore.setTouchEvent();
      pageContentHandler.getTextManagerInstance().handleTouchEnd(stores);
    },
    onMouseDown: event => {
      stores.touchSelStore.setTouchEvent(true);
      event.stopPropagation();
      extendSelection = true;
      stores.touchSelStore.hideContextMenu(true);
      removeMouseUpHandler = addEventListener(document, "mouseup", event => {
        removeMouseUpHandler();
        stores.touchSelStore.showContextMenuDebounced();
        extendSelection = false;
        stores.touchSelStore.setTouchEvent();
      });
    },
    onMouseMove: event => {
      if (extendSelection) {
        handleGrabberMoveHelper(stores, event.pageX, event.pageY);
        event.stopPropagation();
      }
    },
    handleGrabberMove: event => {
      stores.touchSelStore.setTouchEvent(true);
      handleGrabberMoveHelper(stores, event.center.x, event.center.y);
    },
    classStyle: () => {
      let style = {
        backgroundColor: constants.GRABBER_BG_COLOR,
        borderTopLeftRadius: constants.GRABBER_BORDER_RADIUS,
        borderBottomLeftRadius: constants.GRABBER_BORDER_RADIUS,
        borderTopRightRadius: constants.GRABBER_BORDER_RADIUS,
        borderBottomRightRadius: constants.GRABBER_BORDER_RADIUS
      };
      if (isLeftMostQuadValid) {
        const { topLeft, bottomRight } = leftMostQuad;
        const top = topLeft.y;
        const bottom = bottomRight.y;
        const left = topLeft.x;
        const right = bottomRight.x;

        if (top < bottom) {
          if (left < right) {
            delete style.borderBottomRightRadius;
          } else {
            delete style.borderBottomLeftRadius;
          }
        } else {
          if (left < right) {
            delete style.borderTopRightRadius;
          } else {
            delete style.borderTopLeftRadius;
          }
        }
      }
      return style;
    },
    setEventOnGrabber: flag => {
      stores.touchSelStore.setEventOnGrabber(flag);
    }
  };
}

const LeftGrabberContainer = inject(mapStoreToProps)(Grabber);

export { LeftGrabberContainer };
